import type {
    CoverableDTO,
    CoverageDTO,
    TermDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { formatStringToMonetaryValueString, getCoverageFromCoverable } from './coverablesUtil';
import { MVPCOMPREHENSIVE_COV } from '../constants/coverage';

function isTermForcedDeductible(term: TermDTO): boolean {
    const termType = term.publicId ?? '';
    return /Forced.*Deduct/g.test(termType) || /Forced/g.test(termType);
}

function isTermTypeOfDeductible(term: TermDTO): boolean {
    return /DeductibleType/g.test(term.publicId ?? '');
}

function isTermDeductible(term: TermDTO): boolean {
    const termType = term.publicId ?? '';
    return (
        !isTermForcedDeductible(term) &&
        (/Deduct/g.test(termType) ||
            (/Cov.*Ded/g.test(termType) && Number(term.value) !== 0) ||
            /SpecialDed/g.test(termType))
    );
}

function isTermMainDeductible(term: TermDTO, lobName: string): boolean {
    const termType = term.publicId ?? '';
    if (lobName === 'Indboforsikring' || lobName === 'Indboforsikring til unge') {
        const contentTermArray = [
            'OptionPRHLiabilityCovDeductibleLawyerType',
            'PRCLiabilityCovAnimalObject',
            'PRCLiabilityCovTermLimitPerson'
        ];
        return (
            !isTermForcedDeductible(term) &&
            !isTermTypeOfDeductible(term) &&
            !contentTermArray.includes(termType)
        );
    }
    const termArray = [
        'OptionLBVLegalAidCovPercentageDedType',
        'OptionLBVLegalAidCovMinimumDedType',
        'MVPWindscreenProtectionRepairDeductible',
        'MVPWindscreenProtectionDeductible',
        'MVPLiabilityDeductible_Car20',
        'MVPComprehensiveDeductible_Car20',
        'MVPLegalAidDedPercWithLegalCounselling',
        'MVPLeasingcarReturnDeductible',
        'MVPLiabilityDeductibleYoungDriver',
        'MVPComprehensiveDeductibleYoungDriver',

        // private house and holiday home dedutibles
        'PRHHouseRotAndInsectsSpecialDed',
        'PRHHolidayHomeRotAndInsectsSpecialDed',
        'PRHHouseDryrotSpecialDed',
        'PRHHolidayHomeDryrotSpecialDed',
        'PRHLiabilityCovLegalAid',
        'PRHLiabilityCovLegalAidDeductible',
        'PRHLiabilityCovMinimumLawyer',
        'PRHHouseCosmeticDifferencesSpecialDed',
        'PRHHolidayHomeCosmeticDifferencesSpecialDed',
        'PRHPrivateContentCovSumInsuredNew',
        'PRHPrivateContentCovManualSum',
        'PRHPrivateContentCovBike',
        'PRHPrivateContentCovBikeParts',
        'PRHPrivateContentCovBikeClaim',
        'PRHPrivateContentCovSmallBoats',
        'PRHPrivateContentCovRefrigerated',
        'PRHPrivateContentCovPlusDamageDeductible',
        'PRHPrivateContentCovNotice',
        'PRHUnderExtensionCovExtendedArea',
        'PRHUnderExtensionCovExtensionEndDate',
        'PRHWaterProtectionServiceCovLeakbotType',
        'PRHWaterProtectionServiceCovLeakbotID',
        'PRHWaterProtectionServiceWithPlumbing',
        'PRHWaterProtectionServiceNoPlumbing',
        'PRHWaterProtectionServiceFree',
        'PRHWaterDamageCovSubsidy',
        'PRHPestsCovSubsidy'
    ];

    return (
        !isTermForcedDeductible(term) &&
        !isTermTypeOfDeductible(term) &&
        (/CovDeductible|CovDedPerc|CovDedMinimum/g.test(termType) || termArray.includes(termType))
    );
}

function isTermSecondaryDeductible(term: TermDTO, lobName: string): boolean {
    const termsArray = [
        // Private Content
        'OptionPRCContentAndBaggageCovCloudBurstType',
        'OptionPRCContentAndBaggageCovIdentityTheftType',
        'OptionPRCContentAndBaggageCovTraumaCounselingType',
        // Car
        'MVPComprehensiveAudioTelcoEquipent',
        'MVPComprehensiveDeductibleYoungDriver',
        'MVPLeasingcarReturnDamageExclVAT',
        'MVPLeasingcarReturnTotalCompExclVAT',
        'MVPLeasingcarReturnAllDamagesInclVAT',

        // Change of owner
        'CHOStandardCovTrifleLimit',
        'CHOExtendedCovTrifleLimit',
        'CHOStandardCovTotalDeductible',
        'CHOExtendedCovTotalDeductible',
        'CHOStandardCovIncreasedConstructionCosts',
        'CHOExtendedCovIncreasedConstructionCosts',
        'CHOStandardCovMaximumIncreasedConstructionCosts',
        'CHOExtendedCovMaximumIncreasedConstructionCosts'
    ];
    const termType = term.publicId ?? '';
    return (
        !isTermMainDeductible(term, lobName) &&
        !isTermTypeOfDeductible(term) &&
        (isTermDeductible(term) || termsArray.includes(termType))
    );
}

const getDeductiblesText = (
    coverage: CoverageDTO,
    mainDeductibles: TermDTO[],
    secondaryDeductibles: TermDTO[]
): string => {
    const nonDeductibleSingleTerms = ['PRHCosmeticCov'];

    switch (true) {
        case mainDeductibles.length === 0 && secondaryDeductibles.length === 0:
            return 'Ingen selvrisiko';
        case mainDeductibles.length > 1 ||
            secondaryDeductibles.length > 1 ||
            coverage.publicID === 'PRHDryRotCov':
            return 'Varierende selvrisiko';
        case nonDeductibleSingleTerms.includes(coverage.type ?? ''):
            return `${'Egenbetaling: '}${mainDeductibles[0].value ?? ''}`;
        case mainDeductibles.length === 1:
            return `${'Selvrisiko: '}${formatStringToMonetaryValueString(
                mainDeductibles[0].value ?? ''
            )}`;
        default:
            return 'Ingen selvrisiko';
    }
};

function processBodyTerms(terms: TermDTO[], lobName: string): ProcessedTerms {
    const mainDeductibles = terms.filter((term: TermDTO) => isTermMainDeductible(term, lobName));
    const secondaryDeductibles = terms.filter((term: TermDTO) =>
        isTermSecondaryDeductible(term, lobName)
    );

    return {
        mainDeductibles,
        secondaryDeductibles
    };
}
interface ProcessedTerms {
    mainDeductibles: TermDTO[];
    secondaryDeductibles: TermDTO[];
}

const getTermFromCoverage = (
    coverage: CoverageDTO | undefined,
    termId: string
): TermDTO | undefined => {
    return coverage?.terms?.find((term: TermDTO) => term.publicId === termId);
};

const getTermValue = (coverable: CoverableDTO, coverageType: string, termId: string): string => {
    return (
        getTermFromCoverage(getCoverageFromCoverable(coverable.coverages, coverageType), termId)
            ?.value ?? ''
    );
};

const getTermMonetaryValue = (
    coverable: CoverableDTO,
    coverageType: string,
    termId: string
): string => {
    return formatStringToMonetaryValueString(getTermValue(coverable, coverageType, termId));
};

const isInsuranceSumAboveThreshold = (value: string | undefined): boolean => {
    return isNaN(parseInt((value ?? '').split(' ')[0].replaceAll('.', '')));
};

function handleMVPYoungDriverCoverageTerms(
    youngDriverCoverage: CoverageDTO,
    coverages: CoverageDTO[],
    flavourName: string
): void {
    const comprehensiveCov = coverages.find((cov) => cov.type === MVPCOMPREHENSIVE_COV);
    const {
        mainDeductibles: comprehensiveMainDeductibles,
        secondaryDeductibles: comprehensiveSecondaryDeductibles
    } = processBodyTerms(comprehensiveCov?.terms ?? [{}], flavourName);
    youngDriverCoverage.terms = youngDriverCoverage.terms?.concat(
        comprehensiveMainDeductibles,
        comprehensiveSecondaryDeductibles
    );
}

export {
    getDeductiblesText,
    processBodyTerms,
    isTermMainDeductible,
    isTermSecondaryDeductible,
    getTermFromCoverage,
    getTermValue,
    getTermMonetaryValue,
    isInsuranceSumAboveThreshold,
    handleMVPYoungDriverCoverageTerms
};

export type { ProcessedTerms };
